import { isHeicMedia } from "./is-heic-media";

// Leave room for multipart headers below the host's request-body limit.
export const MAX_MOBILE_IMAGE_BYTES = 3 * 1024 * 1024;
export type UploadedMobileImage = { fullPath: string; originalFileName: string };

const imageTypes: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
  webp: "image/webp", gif: "image/gif", heic: "image/heic",
  heif: "image/heif", avif: "image/avif", bmp: "image/bmp",
};

export const getUploadMimeType = (file: { name: string; type?: string }) => {
  const type = file.type?.split(";", 1)[0].trim().toLowerCase();
  if (type && type !== "application/octet-stream") return type;
  return imageTypes[file.name.split(".").pop()?.toLowerCase() ?? ""] ?? type ?? "";
};

export const uniqueUploadName = (name: string, id = crypto.randomUUID()) =>
  `${id}_${name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_")}`;

// Convert library HEIC files before preview/upload. Large camera/library JPEGs
// are resized only for the multipart mobile flow to keep requests manageable.
export const prepareImageUpload = async (file: File, resize = false): Promise<File> => {
  if (!file.size) throw new Error(`“${file.name}” is empty. Please choose another photo.`);
  const type = getUploadMimeType(file);
  let image = file.type === type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
  if (!type.startsWith("image/")) throw new Error(`“${file.name}” is not a supported image. Please choose a photo.`);
  if (isHeicMedia({ name: file.name, contentType: type })) {
    try {
      const { heicTo } = await import("heic-to/csp");
      const jpeg = await heicTo({ blob: image, type: "image/jpeg", quality: 0.9 });
      image = new File([jpeg], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
      throw new Error(`Could not read “${file.name}”. Please export it as a JPEG or take a new photo.`);
    }
  }
  if (!resize) return image;
  if (!["image/jpeg", "image/png", "image/webp"].includes(image.type)) {
    if (image.size > MAX_MOBILE_IMAGE_BYTES) throw new Error(`“${file.name}” is too large. Please choose a JPEG photo instead.`);
    return image;
  }

  const url = URL.createObjectURL(image);
  try {
    const decoded = new Image();
    await new Promise<void>((resolve, reject) => {
      decoded.onload = () => resolve();
      decoded.onerror = () => reject(new Error(`Could not read “${file.name}”. Please choose another photo.`));
      decoded.src = url;
    });
    const ratio = Math.min(1, 2400 / Math.max(decoded.naturalWidth, decoded.naturalHeight));
    if (ratio === 1 && image.size < 1024 * 1024) return image;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(decoded.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(decoded.naturalHeight * ratio));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare this photo. Please try another browser.");
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(decoded, 0, 0, canvas.width, canvas.height);
    let blob: Blob | undefined;
    for (const quality of [0.9, 0.75, 0.6]) {
      blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error("Unable to prepare this photo. Please try again.")),
        "image/jpeg", quality
      ));
      if (blob.size <= MAX_MOBILE_IMAGE_BYTES) break;
    }
    if (!blob || blob.size > MAX_MOBILE_IMAGE_BYTES) throw new Error(`“${file.name}” is too large. Please choose a smaller photo.`);
    return new File([blob], image.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const imageUploadError = (error: any) => {
  const status = error?.statusCode ?? error?.status ?? error?.response?.status;
  if (status === 413) return "These photos are too large to upload together. Please choose smaller photos or fewer pages and try again.";
  if (status === 409) return "This upload link has already been used. Please check your assignment before uploading again.";
  if (status >= 500) return "The server could not finish your upload. Your photos are still here; please try again.";
  return error?.data?.message ?? error?.data?.statusMessage ?? "Upload failed. Check your connection and try again. Your photos are still here.";
};
